import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  UsersIcon,
  Square3Stack3DIcon,
  SparklesIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  TrophyIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';
import './LandingPage.css';

const STEPS = [
  { id: 1, name: 'Organization Profile', icon: BuildingOfficeIcon, xp: 100, achievement: '🏢 Foundation Laid' },
  { id: 2, name: 'Project Setup', icon: BriefcaseIcon, xp: 150, achievement: '🚀 Mission Defined' },
  { id: 3, name: 'Team Configuration', icon: UsersIcon, xp: 200, achievement: '👥 Squad Assembled' },
  { id: 4, name: 'System Integrations', icon: Square3Stack3DIcon, xp: 250, achievement: '🔗 Systems Connected' },
  { id: 5, name: 'AI Features', icon: SparklesIcon, xp: 300, achievement: '✨ AI Activated' },
];

const TOTAL_XP = STEPS.reduce((sum, s) => sum + s.xp, 0);

/* ─── Confetti Launcher ─── */
function launchConfetti() {
  const colors = ['#6366f1', '#22d3ee', '#34d399', '#fbbf24', '#f472b6', '#a78bfa'];
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden;';
  document.body.appendChild(container);

  for (let i = 0; i < 50; i++) {
    const piece = document.createElement('div');
    const color = colors[Math.floor(Math.random() * colors.length)];
    const left = Math.random() * 100;
    const rotation = Math.random() * 1440 - 720;
    const duration = 2 + Math.random() * 2;
    const shape = Math.random() > 0.5 ? '50%' : '0';

    piece.className = 'confetti-piece';
    piece.style.cssText = `
      left: ${left}%;
      background: ${color};
      border-radius: ${shape};
      --rotation: ${rotation}deg;
      --fall-duration: ${duration}s;
      animation-delay: ${Math.random() * 0.5}s;
    `;
    container.appendChild(piece);
  }

  setTimeout(() => container.remove(), 4000);
}

/* ─── XP Progress Bar ─── */
function XPBar({ currentStep, completedSteps }) {
  const earnedXP = STEPS.filter((_, i) => completedSteps.includes(i + 1))
    .reduce((sum, s) => sum + s.xp, 0);
  const percentage = (earnedXP / TOTAL_XP) * 100;
  const currentLevel = completedSteps.length >= 4 ? 'Strategist' :
    completedSteps.length >= 2 ? 'Operator' : 'Hustler';

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <StarIcon className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-gray-700">
            Level: <span className="text-indigo-600">{currentLevel}</span>
          </span>
        </div>
        <span className="text-sm font-medium text-gray-500">
          {earnedXP} / {TOTAL_XP} XP
        </span>
      </div>
      <div className="xp-bar">
        <div className="xp-bar-fill" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

/* ─── Health Score Preview ─── */
function HealthScorePreview({ features }) {
  const enabledCount = Object.values(features).filter(Boolean).length;
  const score = Math.round((enabledCount / Object.keys(features).length) * 40 + 55);
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (circumference * score) / 100;

  return (
    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
      <div className="relative w-20 h-20 flex-shrink-0">
        <svg width="80" height="80" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="50" cy="50" r="45" fill="none" stroke="#e0e7ff" strokeWidth="6" />
          <circle
            cx="50" cy="50" r="45" fill="none"
            stroke="url(#previewGrad)" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
          <defs>
            <linearGradient id="previewGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#22d3ee" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-indigo-700">{score}</span>
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 text-sm">Business Health Score Preview</h4>
        <p className="text-xs text-gray-600 mt-1">
          {enabledCount} AI features enabled. Enable more to boost your score.
        </p>
      </div>
    </div>
  );
}

const INDUSTRIES = [
  'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail',
  'Education', 'Real Estate', 'Media', 'Transportation', 'Energy', 'Other'
];

const COMPANY_SIZES = [
  '1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'
];

const PROJECT_TYPES = [
  'Software Development', 'Mobile App', 'Web Application', 'API Development',
  'Infrastructure', 'Data Analytics', 'AI/ML', 'Cloud Migration', 'Other'
];

const INTEGRATIONS = [
  { id: 'slack', name: 'Slack', icon: '💬' },
  { id: 'jira', name: 'Jira', icon: '🎯' },
  { id: 'github', name: 'GitHub', icon: '🐙' },
  { id: 'google_drive', name: 'Google Drive', icon: '📁' },
  { id: 'microsoft_teams', name: 'Microsoft Teams', icon: '👥' },
  { id: 'asana', name: 'Asana', icon: '✓' },
];

export default function EnterpriseOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [founderMode, setFounderMode] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Organization Profile
    company_name: '',
    industry: '',
    company_size: '',
    website: '',
    contact_name: '',
    contact_email: '',
    contact_role: '',
    contact_phone: '',

    // Step 2: Project Setup
    project_title: '',
    impact_statement: '',
    project_description: '',
    project_budget: '',
    project_timeline: '',
    project_priority: 'high',
    project_type: '',

    // Step 3: Team Configuration
    team_size: '',
    team_structure: '',
    stakeholder_emails: '',

    // Step 4: System Integrations
    integrations: [],

    // Step 5: AI Features
    ai_features_enabled: {
      strategic_intelligence: true,
      value_velocity: true,
      quantum_theory: true,
      proactive_optimization: true,
      advanced_analytics: true,
      notifications: true,
    },
  });

  const onboardingMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/onboarding/complete', data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('🎉 Enterprise onboarding complete!');
      // Navigate to the new project
      if (data.project_id) {
        setTimeout(() => navigate(`/projects/${data.project_id}`), 1500);
      } else {
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Onboarding failed');
    },
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAIFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      ai_features_enabled: {
        ...prev.ai_features_enabled,
        [feature]: !prev.ai_features_enabled[feature],
      },
    }));
  };

  const handleIntegrationToggle = (integrationId) => {
    setFormData(prev => {
      const integrations = prev.integrations.includes(integrationId)
        ? prev.integrations.filter(id => id !== integrationId)
        : [...prev.integrations, integrationId];
      return { ...prev, integrations };
    });
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.company_name || !formData.industry || !formData.contact_name || !formData.contact_email) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!formData.contact_email.includes('@')) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      case 2:
        if (!formData.project_title || !formData.impact_statement) {
          toast.error('Please provide project title and impact statement');
          return false;
        }
        return true;
      case 3:
        if (!formData.stakeholder_emails) {
          toast.error('Please provide at least one stakeholder email');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 5) {
        // Mark step as completed and show achievement
        if (!completedSteps.includes(currentStep)) {
          setCompletedSteps(prev => [...prev, currentStep]);
          const step = STEPS.find(s => s.id === currentStep);
          launchConfetti();
          toast.success(
            `${step.achievement} — +${step.xp} XP!`,
            { duration: 3000, icon: '🏆' }
          );
        }
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      // Convert budget to number
      const submissionData = {
        ...formData,
        project_budget: formData.project_budget ? parseFloat(formData.project_budget) : null,
        onboarding_completed: new Date().toISOString(),
        setup_type: 'enterprise_onboarding',
      };
      onboardingMutation.mutate(submissionData);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrophyIcon className="w-8 h-8 text-amber-500" />
            <h1 className="text-4xl font-bold text-gray-900">
              Begin Your Mission
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Set up your organization for AI-powered project management
          </p>
        </div>

        {/* XP Progress Bar */}
        <XPBar currentStep={currentStep} completedSteps={completedSteps} />

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = currentStep > step.id;
              const isCurrent = currentStep === step.id;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-400'
                      } transition-colors`}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon className="w-6 h-6" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <p className={`mt-2 text-sm font-medium ${
                      isCurrent ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {step.name}
                    </p>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-200'
                    } transition-colors`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Step 1: Organization Profile */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Organization Profile</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Acme Corporation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select industry</option>
                    {INDUSTRIES.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Size
                  </label>
                  <select
                    value={formData.company_size}
                    onChange={(e) => handleInputChange('company_size', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select size</option>
                    {COMPANY_SIZES.map(size => (
                      <option key={size} value={size}>{size} employees</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => handleInputChange('contact_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Role
                  </label>
                  <input
                    type="text"
                    value={formData.contact_role}
                    onChange={(e) => handleInputChange('contact_role', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="CTO, Project Manager, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Project Setup */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Initial Project Setup</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.project_title}
                  onChange={(e) => handleInputChange('project_title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enterprise Mobile Application"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Impact Statement <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.impact_statement}
                  onChange={(e) => handleInputChange('impact_statement', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="What problem does this project solve and what value will it deliver?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description
                </label>
                <textarea
                  value={formData.project_description}
                  onChange={(e) => handleInputChange('project_description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Detailed description of the project scope and objectives..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Type
                  </label>
                  <select
                    value={formData.project_type}
                    onChange={(e) => handleInputChange('project_type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select type</option>
                    {PROJECT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.project_priority}
                    onChange={(e) => handleInputChange('project_priority', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget (GBP)
                  </label>
                  <input
                    type="number"
                    value={formData.project_budget}
                    onChange={(e) => handleInputChange('project_budget', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="100000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeline
                  </label>
                  <input
                    type="text"
                    value={formData.project_timeline}
                    onChange={(e) => handleInputChange('project_timeline', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="6 months, Q1 2025, etc."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Team Configuration */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Team Configuration</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Size
                  </label>
                  <select
                    value={formData.team_size}
                    onChange={(e) => handleInputChange('team_size', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select size</option>
                    <option value="1-5">1-5 members</option>
                    <option value="6-10">6-10 members</option>
                    <option value="11-20">11-20 members</option>
                    <option value="20+">20+ members</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Structure
                  </label>
                  <select
                    value={formData.team_structure}
                    onChange={(e) => handleInputChange('team_structure', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select structure</option>
                    <option value="cross-functional">Cross-functional</option>
                    <option value="specialized">Specialized</option>
                    <option value="matrix">Matrix</option>
                    <option value="agile">Agile Teams</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stakeholder Emails <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.stakeholder_emails}
                  onChange={(e) => handleInputChange('stakeholder_emails', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter stakeholder emails, separated by commas&#10;e.g., john@company.com, jane@company.com, pm@company.com"
                />
                <p className="mt-2 text-sm text-gray-500">
                  These stakeholders will receive project updates and notifications
                </p>
              </div>
            </div>
          )}

          {/* Step 4: System Integrations */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">System Integrations</h2>
              <p className="text-gray-600 mb-6">
                Select the tools you'd like to integrate with your project management system
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {INTEGRATIONS.map(integration => (
                  <button
                    key={integration.id}
                    onClick={() => handleIntegrationToggle(integration.id)}
                    className={`p-6 border-2 rounded-lg transition-all ${formData.integrations.includes(integration.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <div className="text-4xl mb-2">{integration.icon}</div>
                    <div className="font-medium text-gray-900">{integration.name}</div>
                    {formData.integrations.includes(integration.id) && (
                      <CheckCircleIcon className="w-6 h-6 text-blue-500 mt-2 mx-auto" />
                    )}
                  </button>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> You can configure these integrations after onboarding
                  in the Settings page. Some integrations require OAuth authentication.
                </p>
              </div>
            </div>
          )}

          {/* Step 5: AI Features */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">AI-Powered Features</h2>
                {/* Founder Mode Toggle */}
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${!founderMode ? 'text-indigo-600' : 'text-gray-400'}`}>Analyst</span>
                  <button
                    id="onboarding-founder-toggle"
                    onClick={() => setFounderMode(!founderMode)}
                    className={`relative w-10 h-5 rounded-full transition-colors duration-300 ${
                      founderMode ? 'bg-indigo-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
                      founderMode ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <span className={`text-xs font-medium ${founderMode ? 'text-indigo-600' : 'text-gray-400'}`}>Founder</span>
                </div>
              </div>
              <p className="text-gray-600">
                {founderMode
                  ? 'Plain English mode — no jargon, just what matters.'
                  : 'Enable AI capabilities to enhance your project management'}
              </p>

              <div className="space-y-4">
                {Object.entries({
                  strategic_intelligence: {
                    name: founderMode ? 'Smart Advice Engine' : 'Strategic Intelligence',
                    description: founderMode
                      ? 'Get simple, actionable advice about what to do next with your project'
                      : 'AI-driven insights and recommendations for project strategy',
                  },
                  value_velocity: {
                    name: founderMode ? 'Speed Tracker' : 'Value Velocity Tracking',
                    description: founderMode
                      ? 'See how fast your project is moving and if you\'re on track'
                      : 'Real-time monitoring of value delivery and project momentum',
                  },
                  quantum_theory: {
                    name: founderMode ? 'What-If Simulator' : 'Quantum Theory Analysis',
                    description: founderMode
                      ? 'Slide bars to see "what happens if I change this?" — instant profit projections'
                      : 'Multiple scenario planning and probabilistic forecasting',
                  },
                  proactive_optimization: {
                    name: founderMode ? 'Auto Problem Fixer' : 'Proactive Optimization',
                    description: founderMode
                      ? 'AI spots problems before they happen and suggests fixes automatically'
                      : 'Automated identification and resolution of bottlenecks',
                  },
                  advanced_analytics: {
                    name: founderMode ? 'Deep Number Cruncher' : 'Advanced Analytics',
                    description: founderMode
                      ? 'Turn your boring numbers into clear pictures that predict the future'
                      : 'Deep learning-based performance metrics and predictions',
                  },
                  notifications: {
                    name: founderMode ? 'Smart Alerts' : 'Intelligent Notifications',
                    description: founderMode
                      ? 'Only get pinged when something actually matters — no spam'
                      : 'Context-aware alerts and priority-based notifications',
                  },
                }).map(([key, feature]) => (
                  <motion.div
                    key={key}
                    layout
                    className={`flex items-start space-x-4 p-4 border rounded-lg transition-all duration-200 ${
                      formData.ai_features_enabled[key]
                        ? 'border-indigo-300 bg-indigo-50/50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.ai_features_enabled[key]}
                      onChange={() => handleAIFeatureToggle(key)}
                      className="mt-1 w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <AnimatePresence mode="wait">
                        <motion.h3
                          key={founderMode ? 'founder' : 'analyst'}
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 4 }}
                          className="font-medium text-gray-900"
                        >
                          {feature.name}
                        </motion.h3>
                      </AnimatePresence>
                      <AnimatePresence mode="wait">
                        <motion.p
                          key={founderMode ? 'founder-desc' : 'analyst-desc'}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="text-sm text-gray-600 mt-1"
                        >
                          {feature.description}
                        </motion.p>
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Health Score Preview */}
              <HealthScorePreview features={formData.ai_features_enabled} />

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6 mt-4">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <SparklesIcon className="w-5 h-5 text-blue-600 mr-2" />
                  AI-Enhanced Project Management
                </h4>
                <p className="text-sm text-gray-700">
                  Our AI system learns from your projects and continuously improves recommendations.
                  All features can be customized in Settings after onboarding.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Previous
            </button>

            {currentStep < 5 ? (
              <button
                onClick={handleNext}
                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Next
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </button>
            ) : (
              <button
                onClick={() => {
                  // Mark final step as completed with confetti
                  if (!completedSteps.includes(5)) {
                    setCompletedSteps(prev => [...prev, 5]);
                    launchConfetti();
                    toast.success('✨ AI Activated — +300 XP! Mission Ready!', { duration: 4000, icon: '🏆' });
                  }
                  handleSubmit();
                }}
                disabled={onboardingMutation.isPending}
                className="flex items-center px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition-all shadow-lg hover:shadow-xl"
              >
                {onboardingMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Launching Mission...
                  </>
                ) : (
                  <>
                    🚀 Launch Mission
                    <CheckCircleIcon className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
